import React from 'react'
import { FieldProps, getIn } from 'formik'
import clsx from 'clsx'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

type Prefix = { type: 'ICON'; content: IconProp } | { type: 'TEXT'; content: string }

const URL_PROTOCOL = 'https://'

// Leading protocols are stripped for display. The `+` matters: earlier versions of this field
// re-prefixed on every keystroke, so stored values can carry `https://https://…`.
const stripProtocol = (value: string) => value.replace(/^(?:https?:\/\/)+/, '')

// The canonical stored form: exactly one protocol, or empty.
const withProtocol = (value: string) => {
  const bare = stripProtocol(value)
  return bare && `${URL_PROTOCOL}${bare}`
}

const FormikInput: React.ComponentType<
  FieldProps & { label: string; type: string; innerRef: React.RefObject<HTMLInputElement> }
> = ({ field, form, label, ...rest }) => {
  const error = getIn(form.errors, field.name)
  const isTouched = getIn(form.touched, field.name)
  const hasError = isTouched && error

  let prefix: Prefix | null = null
  if (rest.type === 'search') {
    prefix = {
      type: 'ICON',
      content: faSearch,
    }
  } else if (rest.type === 'url') {
    prefix = {
      type: 'TEXT',
      content: 'https://',
    }
  }

  let prefixAddon = null
  if (prefix?.type === 'ICON') {
    prefixAddon = (
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center bg-white pl-3">
        <FontAwesomeIcon className="h-5 w-5 text-gray-400" icon={prefix.content} />
      </div>
    )
  } else if (prefix?.type === 'TEXT') {
    prefixAddon = (
      <span className="inline-flex items-center rounded-l-md bg-white px-3 text-gray-500 sm:text-sm">
        {prefix.content}
      </span>
    )
  }

  const propsOverride: React.InputHTMLAttributes<HTMLInputElement> | undefined =
    rest.type === 'url'
      ? {
          type: 'text',
          onChange: (evt) => {
            form.setFieldValue(field.name, withProtocol(evt.target.value))
          },
          // Normalising here repairs values stored by earlier versions of this field, which the
          // display strips but would otherwise be saved back unchanged.
          onBlur: (evt) => {
            const canonical = withProtocol(`${field.value ?? ''}`)
            if (canonical !== field.value) {
              form.setFieldValue(field.name, canonical)
            }
            field.onBlur(evt)
          },
          value: stripProtocol(`${field.value ?? ''}`),
        }
      : undefined

  return (
    <div>
      <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div
        className={clsx({
          'input relative': true,
          'input-error': hasError,
          'mt-1': label,
        })}
      >
        {prefixAddon}

        <input
          id={field.name}
          className={clsx({
            'block w-full appearance-none rounded-md border-0 bg-white px-3 py-2 placeholder-gray-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400 sm:text-sm':
              true,
            'pl-1': prefix?.type === 'TEXT',
            'pl-10': prefix?.type === 'ICON',
            'text-red-900 placeholder-red-300': hasError,
          })}
          ref={rest.innerRef || null}
          {...field}
          {...rest}
          {...propsOverride}
        />
        {hasError ? (
          <div className="pointer-events-none inset-y-0 flex items-center bg-white pr-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
          </div>
        ) : null}
      </div>
      {hasError ? (
        <p className="mt-1 text-xs text-red-900" id={`${field.name}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  )
}

export default FormikInput
